// GraphiteRender.js
// Builds Graphite /render request URLs (via the GraphProxy controller) with image dimensions decided
// by the viewing client at render time.  Configuration query strings deliberately do not contain
// width/height (see GraphiteQuery.js); this module appends them, and compensates for high-DPI
// displays by scaling the requested pixel dimensions along with Graphite's fontSize and lineWidth
// parameters so the resulting image is crisp but visually identical at any devicePixelRatio.

import { DecodeQueryComponent } from 'appRoot/scripts/GraphiteQuery';

const GRAPHITE_DEFAULT_FONT_SIZE = 10;
const GRAPHITE_DEFAULT_LINE_WIDTH = 1.2;

/**
 * Returns the devicePixelRatio to use for graph rendering, clamped to a sane range.
 * @returns {Number} Device pixel ratio between 1 and 3.
 */
export function GetRenderDpr()
{
	let dpr = window.devicePixelRatio || 1;
	return Math.min(3, Math.max(1, dpr));
}
/**
 * Builds the final query string for a Graphite render request.
 * @param {String} configQueryString Query string from GraphiteConfig (may contain %%ServerName%% and legacy width/height parameters).
 * @param {Object} opts Options:
 *   width, height: {Number} Desired image size in CSS pixels. Required.
 *   serverName: {String} Optional server name substituted into %%ServerName%% placeholders.
 *   dpr: {Number} Optional device pixel ratio override. Defaults to GetRenderDpr().
 *   cacheBust: {String|Number} Optional cache busting token appended as cacheBust=...
 * @returns {String} Query string for the GraphProxy endpoint (no leading "?").
 */
export function BuildRenderQueryString(configQueryString, opts)
{
	let dpr = typeof opts.dpr === "number" ? opts.dpr : GetRenderDpr();
	// Cap the total rendered pixel dimensions so very large viewports on high-DPI displays don't
	// request absurdly large images from the Graphite server.
	const MAX_DIMENSION = 4096;
	if (opts.width * dpr > MAX_DIMENSION)
		dpr = MAX_DIMENSION / opts.width;
	if (opts.height * dpr > MAX_DIMENSION)
		dpr = MAX_DIMENSION / opts.height;
	if (dpr < 1)
		dpr = 1;
	let qs = configQueryString;
	if (opts.serverName)
		qs = qs.replace(/%%ServerName%%/gi, opts.serverName);

	// Walk the existing parameters, dropping any legacy width/height and remembering whether
	// fontSize/lineWidth are explicitly configured so DPI scaling can adjust them in place.
	let parts = [];
	let fontSize = null;
	let lineWidth = null;
	for (let part of qs.split("&"))
	{
		if (!part)
			continue;
		let eq = part.indexOf("=");
		let key = (eq === -1 ? part : part.substr(0, eq)).toLowerCase();
		let value = eq === -1 ? "" : part.substr(eq + 1);
		if (key === "width" || key === "height" || key === "cachebust")
			continue;
		if (key === "fontsize")
		{
			fontSize = parseFloat(value);
			continue;
		}
		if (key === "linewidth")
		{
			lineWidth = parseFloat(value);
			continue;
		}
		parts.push(part);
	}

	parts.push("width=" + Math.max(1, Math.round(opts.width * dpr)));
	parts.push("height=" + Math.max(1, Math.round(opts.height * dpr)));
	if (isNaN(fontSize) || fontSize === null)
		fontSize = GRAPHITE_DEFAULT_FONT_SIZE;
	if (isNaN(lineWidth) || lineWidth === null)
		lineWidth = GRAPHITE_DEFAULT_LINE_WIDTH;
	if (dpr !== 1)
	{
		parts.push("fontSize=" + RoundForQuery(fontSize * dpr));
		parts.push("lineWidth=" + RoundForQuery(lineWidth * dpr));
	}
	else
	{
		// Preserve explicitly-configured values at standard DPI.
		if (fontSize !== GRAPHITE_DEFAULT_FONT_SIZE)
			parts.push("fontSize=" + RoundForQuery(fontSize));
		if (lineWidth !== GRAPHITE_DEFAULT_LINE_WIDTH)
			parts.push("lineWidth=" + RoundForQuery(lineWidth));
	}
	if (typeof opts.cacheBust !== "undefined" && opts.cacheBust !== null)
		parts.push("cacheBust=" + encodeURIComponent(opts.cacheBust));
	return parts.join("&");
}
/**
 * Builds a complete image src URL for a Graphite render request through the GraphProxy controller.
 * @param {String} configQueryString Query string from GraphiteConfig.
 * @param {Object} opts See BuildRenderQueryString.
 * @returns {String} Image src URL.
 */
export function BuildRenderSrc(configQueryString, opts)
{
	return appContext.appPath + "GraphProxy?" + BuildRenderQueryString(configQueryString, opts);
}
/**
 * Extracts the decoded "title" parameter from a render query string, for use as a caption.
 * @param {String} configQueryString Query string from GraphiteConfig.
 * @param {String} serverName Optional server name substituted into %%ServerName%% placeholders.
 * @returns {String} The graph title, or an empty string.
 */
export function GetQueryStringTitle(configQueryString, serverName)
{
	for (let part of configQueryString.split("&"))
	{
		let eq = part.indexOf("=");
		if (eq === -1)
			continue;
		if (part.substr(0, eq).toLowerCase() === "title")
		{
			let value = DecodeQueryComponent(part.substr(eq + 1));
			if (serverName)
				value = value.replace(/%%ServerName%%/gi, serverName);
			return value;
		}
	}
	return "";
}
function RoundForQuery(n)
{
	return Math.round(n * 100) / 100;
}
